import { formatDateISO, type SiteStatus } from '@safetyhub/shared';
import type { Site, SiteAssignmentWithMember } from '../../lib/organizations';
import { siteIndustryLabel } from '../../lib/siteIndustry';

/**
 * 현장 목록의 통계·필터·정렬 파생 — 렌더와 떼어 둔 순수 함수 층.
 *
 * 상단 카드 4장이 목록과 같은 데이터를 다르게 읽고(합계·비율), 툴바가 그 위에 지역·상태를
 * 얹으면서 이 규칙들이 JSX 안에서는 읽히지 않게 됐다. 파생만 여기로 떼어 단위 테스트로
 * 고정한다(`__tests__/sitesView.test.ts`). 멤버 관리의 `membersView.ts`와 같은 층이다.
 */

/** 점검 상태 라벨 중 "점검이 지금 돌고 있다"에 해당하는 값 — 통계 카드 2번의 분자다. */
export const SITE_STATUS_INSPECTING = '점검중';

/**
 * 현장의 **점검 흐름** — 마지막 점검 하나에서 파생한다.
 *
 * 예전에는 점검이 끝나면 그 현장의 최신 보고서 1건의 결재 상태(작성중/결재중/결재완료/반려)까지
 * 이어 붙였는데, 그 보고서는 점검과 무관한 최신 문서(TBM이든 무엇이든)라 현장 라벨이 임의로
 * 흔들렸다. 결재 진행은 결재함·문서관리가 정본 surface라 여기서 뗐다
 * (결정 기록 `2026-08-03-site-status-axes`). 현장의 **저장 상태**는 `Site.status`(생애주기)가
 * 따로 있고, 이 함수는 그 축과 겹치지 않는다.
 */
export function getSiteInspectionStatus(site: Site): {
  label: string;
  color: string;
  description: string;
} {
  const lastInspection = site.inspections?.[0];

  // 점검이 없는 경우
  if (!lastInspection) {
    return { label: '대기', color: 'gray', description: '점검 내역 없음' };
  }

  // 점검 진행 중
  if (lastInspection.status === 'IN_PROGRESS') {
    return { label: SITE_STATUS_INSPECTING, color: 'blue', description: '점검 진행 중' };
  }

  // 점검 검토 대기
  if (lastInspection.status === 'PENDING_REVIEW') {
    return { label: '검토중', color: 'purple', description: '점검 검토 대기' };
  }

  if (lastInspection.status === 'COMPLETED') {
    return { label: '완료', color: 'green', description: '점검 완료' };
  }

  return { label: '대기', color: 'gray', description: '' };
}

/**
 * 현재 유효한 담당자 배정 — 조직을 떠난 멤버십(`member.leftAt`)의 잔존 행은 담당자가 아니다.
 *
 * 목록 API(`sites.service.findAllByOrganization`)의 `include.assignments`에는 where가 없어
 * **떠난 멤버의 배정도 그대로 내려온다**. 반면 서버의 공백 판정은 `member: { leftAt: null }`을
 * 명시적으로 건다 — 배정 행을 복구용으로 남기는 경로가 있기 때문이다. 화면이 그 축을 안 맞추면
 * "담당자 미지정" 배지 옆에 떠난 사람이 '외 N명'으로 계속 세어지는 자기모순이 생긴다.
 * `leftAt`을 세우는 경로는 탈퇴만이 아니다(admin 강제 처리·멤버 dedup·조직 병합도 세운다).
 *
 * 담당자를 읽는 이 화면의 모든 계산(정 담당자·인원 수·이름 검색)이 이 하나를 통과해야 한다.
 */
export function activeAssignees(site: Site): SiteAssignmentWithMember[] {
  return (site.assignments ?? []).filter((a) => !a.member?.leftAt);
}

/**
 * 현장의 **정 담당자** — 없으면 undefined(= 담당자 공백).
 *
 * 판정 축은 서버와 같아야 한다(`common/utils/site-primary-vacancy.util.ts`):
 * **`assignmentType === 'PRIMARY'` 배정이 하나도 없음**이 공백이지, "배정이 0명"이 아니다 —
 * 부 담당자(SECONDARY)만 남은 현장도 공백이고, 서버는 그 현장에 `SITE_PRIMARY_NEEDED`를 쏜다.
 * 화면이 다른 축으로 세면 알림은 왔는데 목록은 멀쩡해 보이는 어긋남이 생긴다.
 *
 * `member.leftAt`(조직을 떠난 멤버십)도 서버와 같이 배제한다. 탈퇴는 배정 행을 남기는
 * 경로가 있어서(복구 시 함께 되살리려고), 이걸 안 걸면 **떠난 사람 이름이 담당자로 남아**
 * 공백이 화면에서 통째로 사라진다 — 이 화면 경고가 필요한 바로 그 상황이다.
 * 목록 응답은 `assignments[].member`를 CompanyMember 행 그대로 실어 `leftAt`이 온다.
 *
 * 두 렌더 분기(데스크톱 표·모바일 카드)가 각자 `.find()`를 쓰면 한쪽만 고쳐지므로 여기로 모은다.
 */
export function findPrimaryAssignee(site: Site): SiteAssignmentWithMember | undefined {
  return activeAssignees(site).find((a) => a.assignmentType === 'PRIMARY');
}

/** 담당자 공백 현장 수 — 상단 요약의 N. 통계 카드와 같이 **필터 이전 전체 목록** 기준. */
export function countSitesWithoutPrimary(sites: Site[]): number {
  return sites.filter((site) => !findPrimaryAssignee(site)).length;
}

/** 통계 카드 4장이 읽는 값 — 전부 **필터 이전의 전체 목록** 기준이다(카드는 페이지 요약). */
export interface SiteStats {
  total: number;
  /** 상태가 '점검중'인 현장 수. */
  inspecting: number;
  unresolvedTotal: number;
  resolvedTotal: number;
  findingsTotal: number;
  /**
   * 미해결 중 위험도가 최상위 등급인 건수 — '시급 대응 필요' 보조 문구의 수다.
   * 등급 판정은 서버가 각 점검의 유효 위험성평가 config로 한다(프론트에 임계값이 없다).
   */
  unresolvedHighTotal: number;
  /** 최근 7일 신규 발견(유입) 건수 합 — flow 지표. */
  found7dTotal: number;
  /** 최근 7일 해소 건수 합 — flow 지표. */
  resolved7dTotal: number;
  /** 해결률(정수 %). 위험요인이 한 건도 없으면 비율 자체가 없어 null이다(0%가 아니다). */
  resolvedRate: number | null;
}

export function buildSiteStats(sites: Site[]): SiteStats {
  let inspecting = 0;
  let unresolvedTotal = 0;
  let resolvedTotal = 0;
  let findingsTotal = 0;
  let unresolvedHighTotal = 0;
  let found7dTotal = 0;
  let resolved7dTotal = 0;

  for (const site of sites) {
    if (getSiteInspectionStatus(site).label === SITE_STATUS_INSPECTING) inspecting += 1;
    const stats = site.findingStats;
    if (!stats) continue;
    unresolvedTotal += stats.unresolved;
    resolvedTotal += stats.resolved;
    findingsTotal += stats.total;
    // 세 필드는 뒤에 붙은 것이라 옛 응답에는 없다 — 없으면 0으로 접는다(카드가 그 줄을 숨긴다).
    unresolvedHighTotal += stats.unresolvedHigh ?? 0;
    found7dTotal += stats.found7d ?? 0;
    resolved7dTotal += stats.resolved7d ?? 0;
  }

  return {
    total: sites.length,
    inspecting,
    unresolvedTotal,
    resolvedTotal,
    findingsTotal,
    unresolvedHighTotal,
    found7dTotal,
    resolved7dTotal,
    // 분자는 집계된 `resolved` 합이다 — `total - unresolved`로 되돌리면 서버 집계가 세 값을
    // 따로 세는 한(진행 중 상태 등) 없는 수를 지어내게 된다.
    resolvedRate: findingsTotal === 0 ? null : Math.round((resolvedTotal / findingsTotal) * 100),
  };
}

/**
 * 주소에서 지역 토큰 — 첫 공백 앞 한 조각("경기도 과천시 …" → "경기도").
 * 행정구역 사전을 두지 않는 이유는 주소가 자유 입력이라서다. 사전에 없는 표기를 버리는
 * 것보다, 사용자가 적은 첫 토큰을 그대로 묶어 주는 편이 필터로서 예측 가능하다.
 */
export function extractRegion(address: string | undefined | null): string | null {
  const token = (address ?? '').trim().split(/\s+/)[0];
  return token ? token : null;
}

/** 지역 필터 선택지 — 지금 목록에 실제로 등장하는 지역만(고를 수 있는데 빈 결과인 값 방지). */
export function regionOptions(sites: Site[]): string[] {
  const regions = new Set<string>();
  for (const site of sites) {
    const region = extractRegion(site.address);
    if (region) regions.add(region);
  }
  return [...regions].sort((a, b) => a.localeCompare(b, 'ko'));
}

/**
 * 상태 필터 선택지 — 지금 목록에 실제로 등장하는 생애주기 값만(지역 필터와 같은 규칙:
 * 고를 수 있는데 결과가 항상 빈 값은 만들지 않는다). 순서는 등장 순이 아니라 생애주기
 * 진행 순으로 고정한다 — 목록이 바뀔 때마다 선택지 순서가 흔들리지 않아야 한다.
 */
const SITE_LIFECYCLE_ORDER: SiteStatus[] = ['ACTIVE', 'PAUSED', 'CLOSED', 'TRANSFER_PENDING'];

export function lifecycleOptions(sites: Site[]): SiteStatus[] {
  const present = new Set(sites.map((site) => site.status));
  return SITE_LIFECYCLE_ORDER.filter((status) => present.has(status));
}

/** 빈 필터는 전체 — 주소가 없는 현장은 지역을 고른 순간 목록에서 빠진다(지역 불명이므로). */
export function matchesRegion(site: Site, region: string): boolean {
  if (!region) return true;
  return extractRegion(site.address) === region;
}

/**
 * 검색 대상: 현장명 + 주소(=지역) + 업종 + 담당자 이름.
 * 담당자는 **현재 유효한 배정**만 — 떠난 사람 이름으로 검색해 나온 현장이 정작 '담당자 미지정'
 * 배지를 달고 있으면 검색 결과가 화면과 모순된다.
 */
export function matchesSiteQuery(site: Site, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    site.name,
    site.address ?? '',
    // 업종 — KSIC 이름 · 없으면 레거시 「업종 / 세부」(`lib/siteIndustry` — 카드 표시와 같은 값)
    siteIndustryLabel(site) ?? '',
    ...activeAssignees(site).map((a) => a.member?.member?.name ?? ''),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export interface SiteFilter {
  /** 검색어. 원문 그대로 넘기면 되고 trim·소문자화는 이 모듈이 한다. */
  query: string;
  /** 지역 필터. 빈 문자열이면 전체. */
  region: string;
  /**
   * 상태 필터 — 저장된 생애주기(`Site.status`) 값. 빈 문자열이면 전체.
   * 파생 라벨이 아니라 enum 값으로 거르므로 라벨 문구를 바꿔도 필터가 깨지지 않는다.
   */
  status: SiteStatus | '';
}

/** 검색·지역·상태는 AND로 겹친다. */
export function filterSites(sites: Site[], filter: SiteFilter): Site[] {
  return sites.filter(
    (site) =>
      (!filter.status || site.status === filter.status) &&
      matchesRegion(site, filter.region) &&
      matchesSiteQuery(site, filter.query),
  );
}

/**
 * 정렬 키. `''`(기본)은 **서버가 준 순서**(등록 최신순)라 비교기가 없다 — 기본이 곧
 * "정렬하지 않음"이어야 이 화면이 예전과 같은 순서로 열린다.
 */
export type SiteSortKey = 'risk' | 'name' | 'recent';
export type SiteSort = '' | SiteSortKey;

function unresolvedCount(site: Site): number {
  return site.findingStats?.unresolved ?? 0;
}

function findingsCount(site: Site): number {
  return site.findingStats?.total ?? 0;
}

/** 마지막 점검 시각(ms). 점검 이력이 없으면 null — 정렬에서 뒤로 민다. */
function lastInspectedAt(site: Site): number | null {
  const createdAt = site.inspections?.[0]?.createdAt;
  return createdAt ? new Date(createdAt).getTime() : null;
}

/**
 * 비교기 — 동률은 0을 반환한다. `Array.prototype.sort`가 안정 정렬이라 동률끼리는
 * 서버 순서가 그대로 남는다(같은 위험도의 현장이 새로고침마다 뒤바뀌지 않는다).
 */
export const SITE_SORTS: Record<SiteSortKey, (a: Site, b: Site) => number> = {
  // 미해결이 많은 순 → 같으면 전체 건수가 많은 순(둘 다 0인 현장끼리는 서버 순서).
  risk: (a, b) => unresolvedCount(b) - unresolvedCount(a) || findingsCount(b) - findingsCount(a),
  name: (a, b) => a.name.localeCompare(b.name, 'ko'),
  recent: (a, b) => {
    const left = lastInspectedAt(a);
    const right = lastInspectedAt(b);
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    return right - left;
  },
};

export const SITE_SORT_OPTIONS: { value: SiteSortKey; label: string }[] = [
  { value: 'risk', label: '위험도 높은 순' },
  { value: 'name', label: '이름순' },
  { value: 'recent', label: '최근 점검순' },
];

/** 원본을 건드리지 않는다(React Query 캐시 배열을 제자리 정렬하면 다른 소비처가 흔들린다). */
export function sortSites(sites: Site[], sort: SiteSort): Site[] {
  if (!sort) return sites;
  return [...sites].sort(SITE_SORTS[sort]);
}

/** 분기 키(`2026-Q3`) — 현지 달력 기준. */
export function getQuarterKey(date: Date): string {
  // `Date.getMonth()`는 실행 환경 시간대를 타서 분기 경계 하루가 어긋난다 —
  // `formatDateISO`(Asia/Seoul 변환)의 YYYY-MM에서 분기를 뽑는다(membersView와 같은 이유).
  const iso = formatDateISO(date);
  return `${iso.slice(0, 4)}-Q${Math.ceil(Number(iso.slice(5, 7)) / 3)}`;
}

/** 이번 분기에 등록된 현장 수 — '전체 현장' 카드의 보조 문구·증감 pill용(시안 `+1`이 이 뜻). */
export function countRegisteredThisQuarter(sites: Site[], now: Date): number {
  const quarter = getQuarterKey(now);
  return sites.filter((site) => site.createdAt && getQuarterKey(new Date(site.createdAt)) === quarter)
    .length;
}

/**
 * 오늘 점검이 있었던 현장 수 — '점검 진행 중' 카드의 보조 문구(시안 "금일 점검 N건 진행").
 * 축은 표의 '마지막 점검'과 동일(마지막 점검의 createdAt, Asia/Seoul 달력일) — 페이지가
 * 점검 목록 자체를 들고 있지 않아 현장당 마지막 점검으로 근사한다(하루 여러 번 점검한
 * 현장도 1로 센다). 0이면 문구를 숨긴다(신호 없으면 미표시 규약).
 */
export function countInspectedToday(sites: Site[], now: Date): number {
  const today = formatDateISO(now);
  return sites.filter((site) => {
    const last = site.inspections?.[0];
    return last?.createdAt && formatDateISO(new Date(last.createdAt)) === today;
  }).length;
}
